'use client'

import axios from 'axios'
import Cookies from 'js-cookie'
import type { ValidateAddressRequest } from '@/types/apiRequest'
import { Currency } from '@/types/currency'
import {
  CreateMeshLinkTokenRequestBody,
  CreateMeshLinkTokenResponse,
  SaveLinkTokenRequestBody,
  SavePaymentOriginatorRequestBody,
  VerifyPaymentOriginatorRequestBody,
} from '@/types/mesh'
import { PaymentSummary, Token } from '@/types/payment'
import { Rate } from '@/types/rate'
import { AddressOption } from '@/types/wallet'
import { BVNK } from '@/utils/constants'
import { calculateFutureDate } from '@/utils/helpers'

const restApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    'x-requester': process.env.NEXT_PUBLIC_APP_URL,
    'x-tag': 'hosted-payments-page',
    'x-source': 'hosted-payments-page',
  },
})

const internalApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_APP_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
})

interface UpdateSummaryPayload {
  payInMethod?: string
  payOutMethod?: string
  currency: string
  protocol?: string
  network?: string
  payOutInstruction?: {
    address: string
    code: string
    protocol?: string
    currency: string
    network?: string
  }
}

export const putUpdateSummary = async (
  uuid: string,
  payload: UpdateSummaryPayload
): Promise<PaymentSummary> => {
  if (!BVNK && payload?.payOutMethod) {
    const [currency, network] = payload.currency.split('-')
    payload.currency = currency
    payload.network = network
    if (payload?.payOutInstruction?.currency) {
      payload.payOutInstruction.network = network
      payload.payOutInstruction.currency = currency
    }
  }

  const res = await restApi.put(`/v1/pay/${uuid}/update/summary`, payload)
  return res?.data
}

export const putAcceptSummary = async ({
  uuid,
  sealedResult,
}: {
  uuid: string
  sealedResult?: string
}): Promise<PaymentSummary> => {
  const res = await restApi.put(`/v1/pay/${uuid}/accept/summary`, {
    successUrl: 'no_url',
    sealedResult: sealedResult,
  })
  return res?.data
}

export const getStatusSummary = async (
  uuid: string,
  sealedSecretResult?: string
): Promise<PaymentSummary> => {
  const res = await restApi.get(`/v1/pay/${uuid}/summary`, {
    headers: {
      ...(sealedSecretResult && { 'sealed-client-results': sealedSecretResult }),
    },
  })
  return res?.data
}

export const validateAddress = async ({
  address,
  currency,
  destinationTag,
  paymentExternalId,
  protocol,
}: ValidateAddressRequest) => {
  if (!BVNK) {
    const [currencyCode, networkCode] = currency.split('-')
    currency = currencyCode
    protocol = networkCode
  }

  return await restApi.put(`/v1/pay/validate`, {
    address: address,
    code: 'crypto',
    currency: currency,
    source: 'hpp',
    paymentExternalId,
    ...(protocol && BVNK && { protocol }),
    ...(protocol && !BVNK && { network: protocol }),
    ...(destinationTag && { tag: destinationTag }),
  })
}

export const getToken = async (uuid: string): Promise<Token> => {
  const token = Cookies.get(uuid)

  if (token) return { token }

  const res = await restApi.get(`${process.env.NEXT_PUBLIC_API_URL_TOKEN}/?uuid=${uuid}`, {
    transformRequest: (data, headers) => {
      delete headers['x-requester']
      delete headers['x-tag']
      delete headers['x-source']
      return data
    },
  })
  const response = res?.data

  const inMinutes = calculateFutureDate({ days: 0, hours: 0, minutes: 15, seconds: 0 })

  Cookies.set(uuid, response.token, { expires: inMinutes })

  return response
}

export const getSpotRate = async (uuid: string): Promise<Rate> => {
  const res = await restApi.get(`/v2/channel/${uuid}/spot`)
  return res?.data
}

export const getCurrencies = async (): Promise<Currency[]> => {
  const res = await restApi.get(`/v1/currencies/crypto`)
  return res?.data
}

interface CreateLinkTokenRequest {
  userId: string
  subClientId?: string
  transactionId?: string
  tag?: string
  addressOptions: AddressOption[]
}

export interface CreateLinkTokenResponse {
  linkToken: string
  status: string | null
  refreshToken: string | null
  expiresInSeconds: number | null
  refreshTokenExpiresInSeconds: number | null
}

/**
 * @deprecated Use createMeshLinkToken instead
 */
export const createLinkToken = async ({
  userId,
  transactionId,
  tag,
  addressOptions,
}: CreateLinkTokenRequest): Promise<CreateLinkTokenResponse> => {
  try {
    const res = await restApi.post(`/v1/connect/tokens`, {
      userId: userId,
      restrictMultipleAccounts: true,
      transferOptions: {
        transactionId: transactionId,
        toAddresses: addressOptions,
        tag: tag,
        fundingOptions: {
          enabled: true,
        },
      },
    })
    return res?.data
  } catch (error) {
    console.error('Error: postLinkToken', error)
    return {
      linkToken: '',
      status: null,
      refreshToken: null,
      expiresInSeconds: null,
      refreshTokenExpiresInSeconds: null,
    }
  }
}

export const createMeshLinkToken = async ({
  userId,
  subClientId,
  transactionId,
  restrictMultipleAccounts,
  addressTag,
  addressOptions,
  verifyWallet,
}: CreateMeshLinkTokenRequestBody): Promise<CreateMeshLinkTokenResponse> => {
  const res = await internalApi.post(`/api/mesh/tokens`, {
    userId,
    subClientId,
    transactionId,
    restrictMultipleAccounts,
    addressTag,
    addressOptions,
    verifyWallet,
  })
  return res?.data
}

export const getLinkTokens = async (
  userId: string,
  networkId: string
): Promise<Omit<SaveLinkTokenRequest, 'paymentId' | 'userId' | 'networkId'>[]> => {
  const res = await restApi.get(`/v1/connect/tokens/${userId}?networkId=${networkId}`)
  return res?.data
}

export interface SaveLinkTokenRequest {
  paymentId: string
  userId: string
  networkId: string
  accountId: string
  accountName: string
  accessToken: string
  refreshToken: string | null
  expiresInSeconds: number | null
  refreshTokenExpiresInSeconds: number | null
  brokerType: string
  brokerName: string
  brokerBrandInfo: {
    brokerLogoUrl: string
    brokerLogo: string
  }
}

/**
 * @deprecated Use saveMeshLinkToken instead
 */
export const saveLinkToken = async ({
  paymentId,
  userId,
  networkId,
  accountId,
  accountName,
  accessToken,
  refreshToken,
  expiresInSeconds,
  refreshTokenExpiresInSeconds,
  brokerType,
  brokerName,
  brokerBrandInfo,
}: SaveLinkTokenRequest): Promise<SaveLinkTokenRequest> => {
  const res = await restApi.post(`/v1/connect/tokens/save`, {
    paymentId,
    userId,
    networkId,
    accountId,
    accountName,
    accessToken,
    refreshToken,
    expiresInSeconds,
    refreshTokenExpiresInSeconds,
    brokerType,
    brokerName,
    brokerBrandInfo,
  })
  return res?.data
}

export const saveMeshLinkToken = async ({
  userId,
  networkId,
  accountId,
  transactionId,
  accountName,
  accessToken,
  refreshToken,
  expiresInSeconds,
  refreshTokenExpiresInSeconds,
  brokerType,
  brokerName,
  brokerBrandInfo,
}: SaveLinkTokenRequestBody): Promise<void> => {
  await internalApi.post(`/api/mesh/tokens/save`, {
    userId,
    transactionId,
    networkId,
    accountId,
    accountName,
    accessToken,
    refreshToken,
    expiresInSeconds,
    refreshTokenExpiresInSeconds,
    brokerType,
    brokerName,
    brokerBrandInfo,
  })
}

export const verifyPaymentOriginator = async ({
  userId,
  accessToken,
  transactionId,
  integrationBroker,
}: VerifyPaymentOriginatorRequestBody): Promise<void> => {
  await internalApi.post(`/api/mesh/payment-originator/verify`, {
    userId,
    accessToken,
    transactionId,
    integrationBroker,
  })
}

export const savePaymentOriginator = async ({
  userId,
  transactionId,
  verifiedAddress,
  isVerified,
}: SavePaymentOriginatorRequestBody): Promise<void> => {
  await internalApi.post(`/api/mesh/payment-originator/save`, {
    userId,
    transactionId,
    verifiedAddress,
    isVerified,
  })
}
